import { useEffect, useRef, useState } from "react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { fetchAgreementDocuments } from "../../../api/agreementDocuments";
import { showUserNotice } from "../../../api/errors";
import {
  mergeSavedAgreementDocuments,
  getDefaultAgreementDocuments,
  type AgreementDocumentModel,
} from "../utils/defaultAgreementDocuments";
import {
  formatEnrollmentAgreementPartyALine,
  formatEnrollmentAgreementPartyBLine,
} from "../utils/agreementContent";

export function pickEnrollmentAgreementDoc(merged: AgreementDocumentModel[]): AgreementDocumentModel {
  const active = merged.filter((d) => d.isActive);
  const signed = active.find((d) => d.requireSign);
  if (signed) return signed;
  const disc = active.find((d) => d.type === "disclaimer");
  if (disc) return disc;
  return active[0] ?? getDefaultAgreementDocuments()[1];
}

export type AgreementSnapshotPayload = {
  title?: string | null;
  content?: string | null;
  version?: string | null;
};

type Props = {
  tenantLegacyId: string;
  /** 顶栏、印章等使用的协会简称 */
  orgLabel: string;
  memberName: string;
  /** 会员编号（入会申请或档案） */
  memberNo?: string | null;
  idNumber?: string | null;
  phone?: string | null;
  signDate: string;
  sealLine1: string;
  sealLine2: string;
  onClose: () => void;
  /** 审核详情用「完整协议…」，会员详情用「范本预览…」 */
  variant: "audit" | "member";
  /** 申请人实际上传的签名图（完整 URL）；有则替换底部示意笔迹 */
  signatureImageUrl?: string | null;
  /** 会员详情：是否已匹配到已审核通过的入会申请（用于底部说明区分「无申请」与「有申请无签名图」） */
  enrollmentMatched?: boolean;
  /** 若有正文，协议标题与正文以申请存档为准，不再拉取后台「协议与文书」当前配置 */
  agreementSnapshot?: AgreementSnapshotPayload | null;
};

export default function AgreementEnrollmentPreviewModal({
  tenantLegacyId,
  orgLabel,
  memberName,
  memberNo,
  idNumber,
  phone,
  signDate,
  sealLine1,
  sealLine2,
  onClose,
  variant,
  signatureImageUrl,
  enrollmentMatched = false,
  agreementSnapshot = null,
}: Props) {
  const [doc, setDoc] = useState<AgreementDocumentModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [sigImgFailed, setSigImgFailed] = useState(false);
  const [pdfExporting, setPdfExporting] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);

  const snapContent = agreementSnapshot?.content?.trim() ?? "";

  useEffect(() => {
    let cancelled = false;
    if (snapContent) {
      const o = orgLabel.trim() || "本协会";
      setDoc({
        id: "application-snapshot",
        title: (agreementSnapshot?.title?.trim() || `${o}入会协议`) as string,
        type: "disclaimer",
        content: snapContent,
        requireSign: true,
        version: (agreementSnapshot?.version?.trim() || "申请时版本") as string,
        updatedAt: "",
        isActive: true,
      });
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }
    setLoading(true);
    fetchAgreementDocuments(tenantLegacyId)
      .then((rows) => {
        if (cancelled) return;
        setDoc(pickEnrollmentAgreementDoc(mergeSavedAgreementDocuments(rows)));
      })
      .catch(() => {
        if (cancelled) return;
        setDoc(pickEnrollmentAgreementDoc(mergeSavedAgreementDocuments([])));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantLegacyId, snapContent, agreementSnapshot?.title, agreementSnapshot?.version, orgLabel]);

  const o = orgLabel.trim() || "本协会";
  const sigUrl = signatureImageUrl?.trim() || null;

  useEffect(() => {
    setSigImgFailed(false);
  }, [sigUrl]);

  const showSigPhoto = Boolean(sigUrl && !sigImgFailed);
  const headerTitle = doc?.title?.trim() || `${o}入会协议`;
  const versionLabel = (agreementSnapshot?.version?.trim() || doc?.version?.trim() || "").trim();
  const partyALine = formatEnrollmentAgreementPartyALine(o);
  const partyBLine = formatEnrollmentAgreementPartyBLine({
    memberNo,
    name: memberName,
    idNumber,
    phone,
  });
  const sub =
    snapContent
      ? `${doc?.version ?? "—"} · 申请时存档正文${sigUrl ? "及签名" : ""}`
      : variant === "audit"
        ? `${doc?.version ?? "—"} · 完整协议文本及签名`
        : sigUrl
          ? `${doc?.version ?? "—"} · 存档签署（含电子签名图）`
          : `${doc?.version ?? "—"} · 当前文书正文（无存档签名时签名为示意）`;

  const exportAgreementPdf = async () => {
    const el = printRef.current;
    if (!el || loading) return;
    setPdfExporting(true);
    try {
      const canvas = await html2canvas(el, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false,
        backgroundColor: "#ffffff",
        windowWidth: el.scrollWidth,
        windowHeight: el.scrollHeight,
      });
      const pageWPt = 595.28;
      const margin = 40;
      const imgW = pageWPt - margin * 2;
      const imgH = (canvas.height * imgW) / canvas.width;
      const pdfH = Math.max(841.89, imgH + margin * 2);
      const pdf = new jsPDF({ unit: "pt", format: [pageWPt, pdfH], orientation: "portrait" });
      const imgData = canvas.toDataURL("image/jpeg", 0.92);
      pdf.addImage(imgData, "JPEG", margin, margin, imgW, imgH, undefined, "FAST");
      const safeName = memberName.replace(/[/\\?%*:|"<>]/g, "_").trim().slice(0, 48) || "申请人";
      pdf.save(`入会协议存档-${safeName}.pdf`);
    } catch {
      showUserNotice("导出 PDF 失败，请稍后重试。若含签名图，请确认图片地址允许跨域加载。");
    } finally {
      setPdfExporting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0 bg-red-600">
          <div className="flex items-center gap-3 min-w-0">
            <i className="ri-file-text-line text-white text-xl flex-shrink-0"></i>
            <div className="min-w-0">
              <h3 className="font-bold text-white text-sm truncate">《{headerTitle}》</h3>
              <p className="text-red-200 text-xs mt-0.5 truncate">{sub}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-red-500 cursor-pointer text-white flex-shrink-0"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 text-sm text-gray-700 leading-relaxed space-y-4">
          {loading ? (
            <div className="py-16 flex flex-col items-center justify-center text-gray-400 text-sm gap-2">
              <i className="ri-loader-4-line animate-spin text-2xl"></i>
              正在加载协议文书…
            </div>
          ) : (
            <div ref={printRef} className="space-y-4 bg-white">
              <div className="border-b border-gray-100 pb-4">
                <h2 className="text-center text-base font-bold text-gray-900">{headerTitle}</h2>
                <div className="mt-3 space-y-1 text-xs leading-relaxed text-gray-600">
                  <p>{partyALine}</p>
                  <p>{partyBLine}</p>
                  {versionLabel ? <p className="text-gray-500">版本：{versionLabel}</p> : null}
                </div>
              </div>
              <div className="whitespace-pre-wrap text-gray-700">{doc?.content ?? ""}</div>
              <div className="border-t border-gray-200 pt-5 mt-4">
                <p className="text-xs text-gray-500 mb-3 text-center">
                  本人已仔细阅读并理解上述全部条款，自愿签署本入会协议。
                </p>
                <div className="flex items-end justify-between gap-6">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-400 mb-1">申请人手写签名</p>
                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 flex items-center justify-center overflow-hidden h-20 box-border">
                      {showSigPhoto ? (
                        <img
                          key={sigUrl ?? "sig"}
                          src={sigUrl!}
                          alt={`${memberName} 的电子签名`}
                          className="max-h-full max-w-full w-auto object-contain"
                          onError={() => setSigImgFailed(true)}
                        />
                      ) : (
                        <svg viewBox="0 0 300 80" className="h-12 w-full max-w-[200px]" preserveAspectRatio="xMidYMid meet">
                          <path
                            d="M20,55 C35,35 42,20 58,40 C68,52 72,38 88,30 C102,24 106,48 122,40 C134,34 138,50 154,42 C164,38 168,52 184,44 C194,38 200,56 218,46 L234,36 C246,28 250,46 264,38 C274,32 282,52 296,44"
                            stroke="#1a1a1a"
                            strokeWidth="2"
                            fill="none"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1 text-center font-medium">{memberName}</p>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-400 mb-1">签署日期</p>
                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 flex items-center justify-center h-20 box-border">
                      <p className="text-sm font-medium text-gray-800">{signDate}</p>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-400 mb-1">协会盖章</p>
                    <div className="bg-red-50 border border-red-200 rounded-xl p-2.5 h-20 box-border">
                      <div className="relative h-full w-full flex items-center justify-center">
                        <p className="absolute top-6 text-sm font-semibold text-gray-900 text-center leading-tight break-all px-2">
                          {o}
                        </p>
                        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-12 h-12 rounded-full border-2 border-red-500 bg-white/70 flex flex-col items-center justify-center flex-shrink-0">
                          <p className="text-[7px] text-red-600 font-bold text-center leading-tight">{sealLine1}</p>
                          <p className="text-[7px] text-red-600 text-center leading-tight">{sealLine2}</p>
                          <p className="text-[7px] text-red-500 text-center">✦公章✦</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-gray-100 flex-shrink-0 bg-gray-50 flex items-center justify-between gap-3">
          <p className="text-xs text-gray-400">
            {snapContent
              ? showSigPhoto
                ? "正文为申请存档；签名为该次申请上传的电子签名。"
                : "正文为申请存档；签名区如无图为未上传或加载失败。"
              : variant === "audit" && (!sigUrl || sigImgFailed)
                ? sigImgFailed
                  ? "签名图片加载失败，请以审核详情中的附件为准。"
                  : "协议正文为当前后台配置版本；签名区如无实拍图为申请人未上传或示意笔迹。"
                  : variant === "member"
                  ? sigImgFailed
                    ? "签名图加载失败，请核对 OSS 或入会申请附件。"
                    : showSigPhoto
                      ? "协议正文为当前后台文书；签名为入会申请存档的电子签名。"
                      : enrollmentMatched
                        ? "已匹配入会申请但未上传签名图；签名区为示意。"
                        : "未匹配到入会申请存档，签署日为会员加入日；签名区为示意。"
                  : "此协议已由申请人完成电子签署，具有法律效力"}
          </p>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              disabled={loading || pdfExporting}
              onClick={() => void exportAgreementPdf()}
              className="px-4 py-2 border border-gray-300 bg-white text-gray-800 text-sm rounded-xl cursor-pointer hover:bg-gray-50 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
            >
              {pdfExporting ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-file-pdf-line" />}
              {pdfExporting ? "生成中…" : "导出 PDF"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2 bg-red-600 text-white text-sm rounded-xl cursor-pointer hover:bg-red-700 whitespace-nowrap flex items-center justify-center"
            >
              关闭预览
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
